export const GA_MEASUREMENT_ID = 'G-DRE08T2ERJ'
export const ANALYTICS_CONSENT_KEY = 'mythweave-analytics-consent'

export type AnalyticsConsent = 'granted' | 'denied' | null

type Gtag = (...args: unknown[]) => void

export type AnalyticsEventName =
  | 'explore_start'
  | 'search_select'
  | 'node_view'
  | 'relationship_view'
  | 'relationship_bundle_view'
  | 'node_expand'
  | 'collection_open'
  | 'collection_view_atlas'
  | 'collection_clear'
  | 'dataset_switch'
  | 'story_catalog_open'
  | 'story_open'
  | 'story_step_view'
  | 'story_complete'
  | 'story_exit'
  | 'graph_scope_change'
  | 'view_all_connections'
  | 'share_state'
  | 'graph_image_share'
  | 'language_change'
  | 'node_filter_change'
  | 'node_type_highlight'
  | 'relationship_label_change'
  | 'spoiler_reveal'

type AnalyticsParameter = string | number | boolean
export type AnalyticsEventParameters = Record<string, AnalyticsParameter | undefined>

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: Gtag
  }
}

const deniedConsent = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
} as const

export function getAnalyticsConsent(): AnalyticsConsent {
  const stored = window.localStorage.getItem(ANALYTICS_CONSENT_KEY)
  return stored === 'granted' || stored === 'denied' ? stored : null
}

export function saveAnalyticsConsent(consent: Exclude<AnalyticsConsent, null>) {
  window.localStorage.setItem(ANALYTICS_CONSENT_KEY, consent)
}

/**
 * Sends one structured product event only after an explicit analytics opt-in.
 * Callers must use stable IDs and categorical values, never free-form search or
 * profile text. Returning a boolean keeps consent behavior directly testable.
 */
export function trackEvent(name: AnalyticsEventName, parameters: AnalyticsEventParameters = {}) {
  if (getAnalyticsConsent() !== 'granted' || typeof window.gtag !== 'function') return false

  const definedParameters = Object.fromEntries(
    Object.entries(parameters).filter((entry): entry is [string, AnalyticsParameter] => entry[1] !== undefined),
  )
  window.gtag('event', name, definedParameters)
  return true
}

export function enableGoogleAnalytics() {
  const disableKey = `ga-disable-${GA_MEASUREMENT_ID}`
  ;(window as unknown as Record<string, unknown>)[disableKey] = false

  window.dataLayer ??= []
  window.gtag ??= function queueGoogleTag(..._args: unknown[]) {
    window.dataLayer!.push(arguments)
  }

  if (document.querySelector(`script[data-mythweave-ga="${GA_MEASUREMENT_ID}"]`)) return

  window.gtag('consent', 'default', deniedConsent)
  window.gtag('consent', 'update', { ...deniedConsent, analytics_storage: 'granted' })
  window.gtag('js', new Date())
  window.gtag('config', GA_MEASUREMENT_ID, { anonymize_ip: true })

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
  script.dataset.mythweaveGa = GA_MEASUREMENT_ID
  document.head.appendChild(script)
}

export function disableGoogleAnalytics() {
  window.gtag?.('consent', 'update', deniedConsent)
  ;(window as unknown as Record<string, unknown>)[`ga-disable-${GA_MEASUREMENT_ID}`] = true
  document.querySelector(`script[data-mythweave-ga="${GA_MEASUREMENT_ID}"]`)?.remove()
}
